import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import { getExpenseTheme } from './expenseGroupTheme';
import ExpenseListHistory from './ExpenseListHistory';
import ReceiptFileUpload from './ReceiptFileUpload';
import WorkOrderLinkSelect from '../WorkOrders/WorkOrderLinkSelect';
import { Building2, Layers, List, Plus, X } from 'lucide-react';
import SettleFromWalletModal from '../shared/SettleFromWalletModal';
import { MODAL_SECTIONS } from '../shared/modalTheme';
import ExpenseWalletSettlementSection from './ExpenseWalletSettlementSection';
import AppSelect from '../shared/AppSelect';

const fieldBase =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm';
const labelCls = 'mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide';
const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Card', 'Other'];

function PersonVendorField({
  theme,
  nameDropdownRef,
  nameSearchText,
  onNameSearchChange,
  showNameDropdown,
  setShowNameDropdown,
  filteredNames,
  onNameSelect,
  personType,
  personName,
  hasSelection,
  onClear,
}) {
  return (
    <div>
      <label className={labelCls}>Person / vendor (optional)</label>
      <div className="relative" ref={nameDropdownRef}>
        <input
          type="text"
          name="nameSearchText"
          value={nameSearchText}
          onChange={onNameSearchChange}
          onFocus={() => setShowNameDropdown(true)}
          placeholder="Search employee or vendor…"
          className={`${fieldBase} ${theme.fieldFocus}`}
        />
        {showNameDropdown && filteredNames.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-auto ring-1 ring-black/5">
            {filteredNames.map((item, idx) => (
              <button
                key={`${item.type}-${item.id}-${idx}`}
                type="button"
                onClick={() => onNameSelect(item.type, item.id, item.name)}
                className={`w-full text-left px-3 py-2.5 text-sm ${theme.dropdownHover} font-medium text-gray-800`}
              >
                <div>{item.display}</div>
                <div className="text-xs text-gray-500">{item.type === 'employee' ? 'Employee' : 'Vendor/Customer'}</div>
              </button>
            ))}
          </div>
        )}
        {hasSelection && (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {personType !== 'None' && personName && (
        <p className="mt-1 text-xs text-gray-500">Selected: {personName}</p>
      )}
    </div>
  );
}

export default function GroupedExpensesTab({
  variant,
  companyCurrency,
  companyCtx,
  db,
  categoryHeads,
  constantEntries,
  expenseForm,
  editingExpenseId,
  editingExpense,
  handleExpenseFormChange,
  handleEditingExpenseChange,
  handleAddExpense,
  handleSaveExpense,
  handleCancelEditExpense,
  tableFilteredExpenses,
  totalAmount,
  searchQuery,
  dateFilter,
  amountFilter,
  handleSearchChange,
  handleDateChange,
  handleAmountChange,
  clearAllFilters,
  tablePagination,
  sortConfig,
  tableHandleSort,
  showMobileFilters,
  setShowMobileFilters,
  isCardExpanded,
  toggleCard,
  handleEditExpense,
  handleDeleteExpense,
  setExpenseForm,
  setEditingExpense,
  showPersonVendor = false,
  filteredNames = [],
  showNameDropdown,
  setShowNameDropdown,
  nameDropdownRef,
  nameSearchText,
  handleNameSelect,
  employees = [],
  onSettleFromWallet,
  settleExpenseModal,
  setSettleExpenseModal,
  settleExpenseDate,
  setSettleExpenseDate,
  settleExpensePaymentMode,
  setSettleExpensePaymentMode,
  settleExpenseEmployeeId,
  setSettleExpenseEmployeeId,
  settleExpenseEmployeeBalance,
  settleExpenseRemark,
  setSettleExpenseRemark,
  settleExpenseSubmitting,
  handleConfirmSettleExpenseFromWallet,
  recordSettleFromWallet,
  setRecordSettleFromWallet,
  selectedEmployeeForCashFlow,
  setSelectedEmployeeForCashFlow,
  expenseSourceEmployeeBalance,
}) {
  const theme = getExpenseTheme(variant);
  const modalSection =
    variant === 'variable' ? MODAL_SECTIONS.EXPENSES_VARIABLE : MODAL_SECTIONS.EXPENSES_FIXED;
  const [view, setView] = useState('entry');
  const isEdit = Boolean(editingExpenseId);
  const form = isEdit ? editingExpense : expenseForm;
  const onChange = isEdit ? handleEditingExpenseChange : handleExpenseFormChange;
  const head = form.head || '';
  const field = `${fieldBase} ${theme.fieldFocus}`;
  const Icon = variant === 'variable' ? Layers : Building2;

  const constantOptions = head && constantEntries[head] ? constantEntries[head] : [];

  const hasPersonSelection =
    showPersonVendor &&
    form.personType !== 'None' &&
    (form.employeeId || form.partyId);

  const clearPerson = () => {
    const cleared = { personType: 'None', employeeId: '', partyId: '', personName: '' };
    setExpenseForm((f) => ({ ...f, ...cleared }));
    setEditingExpense((f) => ({ ...f, ...cleared }));
  };

  const categoryFields = (
    <>
      <div>
        <label className={labelCls}>Category</label>
        <AppSelect
          name="head"
          value={form.head || ''}
          onChange={onChange}
          placeholder="Select category…"
          aria-label="Category"
          sheetTitle="Category"
          options={[
            { value: '', label: 'Select category…' },
            ...categoryHeads.map((h) => ({ value: h, label: h })),
          ]}
          triggerClassName={`${field} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
        />
      </div>
      <div>
        <label className={labelCls}>Constant entry</label>
        <AppSelect
          name="constantEntry"
          value={form.constantEntry || ''}
          onChange={onChange}
          placeholder="Select entry…"
          aria-label="Constant entry"
          sheetTitle="Constant entry"
          disabled={!head}
          options={[
            { value: '', label: 'Select entry…' },
            ...constantOptions.map((entry) => ({ value: entry, label: entry })),
          ]}
          triggerClassName={`${field} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
        />
      </div>
    </>
  );

  const entryCount = tableFilteredExpenses?.length ?? 0;

  return (
    <div className="mb-6 space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
        <div className={`bg-gradient-to-r ${theme.header} px-5 py-5 text-white`}>
          <div className="flex items-start gap-3">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl backdrop-blur-sm ${theme.iconTile}`}
            >
              <Icon className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">{theme.title}</h2>
              <p className="text-slate-300 text-sm mt-0.5">{theme.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={theme.subNavWrap}>
        <button
          type="button"
          onClick={() => setView('entry')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'entry' ? theme.subNavActive : theme.subNavIdle
          }`}
        >
          <Plus className="h-4 w-4" />
          {isEdit ? 'Edit entry' : 'New expense'}
        </button>
        <button
          type="button"
          onClick={() => setView('history')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'history' ? theme.subNavActive : theme.subNavIdle
          }`}
        >
          <List className="h-4 w-4" />
          All expenses
          {entryCount > 0 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                view === 'history' ? theme.subNavBadgeActive : theme.subNavBadgeIdle
              }`}
            >
              {entryCount}
            </span>
          )}
        </button>
      </div>

      {view === 'entry' && (
        <form
          className="bg-white rounded-xl border border-gray-200 shadow-lg ring-1 ring-black/5 overflow-hidden"
          onSubmit={isEdit ? (e) => e.preventDefault() : handleAddExpense}
        >
          <div className={`h-1 bg-gradient-to-r ${theme.btn}`} aria-hidden />
          <div className="p-5 sm:p-6 space-y-6">
            {isEdit && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
                Editing an existing entry — save or cancel below.
              </div>
            )}

            <section>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className={`h-6 w-1 rounded-full ${theme.stripe}`} />
                Date & category
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Date</label>
                  <input name="date" type="date" value={form.date || ''} onChange={onChange} className={field} required />
                </div>
                {categoryFields}
              </div>
            </section>

            {showPersonVendor && (
              <section>
                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className={`h-6 w-1 rounded-full ${theme.stripeAlt}`} />
                  Person / vendor
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PersonVendorField
                    theme={theme}
                    nameDropdownRef={nameDropdownRef}
                    nameSearchText={nameSearchText}
                    onNameSearchChange={handleExpenseFormChange}
                    showNameDropdown={showNameDropdown}
                    setShowNameDropdown={setShowNameDropdown}
                    filteredNames={filteredNames}
                    onNameSelect={handleNameSelect}
                    personType={form.personType}
                    personName={form.personName}
                    hasSelection={hasPersonSelection}
                    onClear={clearPerson}
                  />
                </div>
              </section>
            )}

            <section>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className={`h-6 w-1 rounded-full ${theme.stripe}`} />
                Payment details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Amount</label>
                  <input
                    name="amount"
                    type="number"
                    placeholder="0.00"
                    value={form.amount || ''}
                    onChange={onChange}
                    className={field}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Payment mode</label>
                  <AppSelect
                    name="paymentMode"
                    value={form.paymentMode || 'Cash'}
                    onChange={onChange}
                    aria-label="Payment mode"
                    sheetTitle="Payment mode"
                    options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))}
                    triggerClassName={`${field} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className={labelCls}>Description</label>
                  <input
                    name="description"
                    placeholder="Optional note"
                    value={form.description || ''}
                    onChange={onChange}
                    className={field}
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <ReceiptFileUpload
                    variant={variant}
                    onChange={onChange}
                    file={form.receipt}
                    existingReceipt={isEdit ? editingExpense?.receiptData : null}
                  />
                </div>
              </div>
            </section>

            {!isEdit && (
              <ExpenseWalletSettlementSection
                variant={variant}
                amount={form.amount}
                paymentMode={form.paymentMode}
                employees={employees}
                recordSettleFromWallet={recordSettleFromWallet}
                setRecordSettleFromWallet={setRecordSettleFromWallet}
                selectedEmployeeId={selectedEmployeeForCashFlow}
                setSelectedEmployeeId={setSelectedEmployeeForCashFlow}
                employeeBalance={expenseSourceEmployeeBalance}
              />
            )}

            {showPersonVendor && db && companyCtx && (
              <WorkOrderLinkSelect
                db={db}
                companyCtx={companyCtx}
                workOrderId={form.workOrderId}
                phaseId={form.phaseId}
                costCategory={form.costCategory}
                onChange={(v) => {
                  if (isEdit) setEditingExpense((prev) => ({ ...prev, ...v }));
                  else setExpenseForm((prev) => ({ ...prev, ...v }));
                }}
              />
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-gray-100">
              {form.amount ? (
                <p className="text-sm text-gray-600">
                  Amount:{' '}
                  <span className={`text-lg font-bold tabular-nums ${theme.amount}`}>
                    {formatCurrency(Number(form.amount) || 0, companyCurrency)}
                  </span>
                </p>
              ) : (
                <span />
              )}
              <div className="flex flex-wrap gap-3 justify-end">
                {isEdit ? (
                  <>
                    <button
                      type="button"
                      onClick={handleCancelEditExpense}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveExpense(editingExpenseId)}
                      className={`inline-flex items-center gap-2 rounded-lg bg-gradient-to-r ${theme.btn} px-5 py-2.5 text-sm font-semibold text-white shadow-md ${theme.btnRing} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                      Save changes
                    </button>
                  </>
                ) : (
                  <button
                    id={variant === 'fixed' ? 'add-expense-button' : undefined}
                    type="submit"
                    className={`inline-flex items-center gap-2 rounded-lg bg-gradient-to-r ${theme.btn} px-5 py-2.5 text-sm font-semibold text-white shadow-md ${theme.btnRing} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                  >
                    <Plus className="h-4 w-4" />
                    {theme.addLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      )}

      {view === 'history' && (
        <ExpenseListHistory
              variant={variant}
              filteredExpenses={tableFilteredExpenses}
              companyCurrency={companyCurrency}
              totalAmount={totalAmount}
              searchQuery={searchQuery}
              dateFilter={dateFilter}
              amountFilter={amountFilter}
              onSearchChange={handleSearchChange}
              onDateChange={handleDateChange}
              onAmountChange={handleAmountChange}
              onClearFilters={clearAllFilters}
              tablePagination={tablePagination}
              sortConfig={sortConfig}
              tableHandleSort={tableHandleSort}
              showMobileFilters={showMobileFilters}
              setShowMobileFilters={setShowMobileFilters}
              isCardExpanded={isCardExpanded}
              toggleCard={toggleCard}
              onEdit={handleEditExpense}
              onDelete={handleDeleteExpense}
              showPersonColumn={showPersonVendor}
              onSettleFromWallet={onSettleFromWallet}
        />
      )}

      {settleExpenseModal && settleExpenseModal.group === variant && (
        <SettleFromWalletModal
          open
          modalSection={modalSection}
          summaryPrimary={`${settleExpenseModal.head || 'Expense'} · ${formatCurrency(
            Number(settleExpenseModal.amount),
            companyCurrency
          )}`}
          summarySecondary={[
            settleExpenseModal.constantEntry,
            settleExpenseModal.personName,
            settleExpenseModal.date,
          ]
            .filter(Boolean)
            .join(' · ')}
          amount={settleExpenseModal.amount}
          employees={employees}
          companyCurrency={companyCurrency}
          settleDate={settleExpenseDate}
          setSettleDate={setSettleExpenseDate}
          settlePaymentMode={settleExpensePaymentMode}
          setSettlePaymentMode={setSettleExpensePaymentMode}
          settleEmployeeId={settleExpenseEmployeeId}
          setSettleEmployeeId={setSettleExpenseEmployeeId}
          settleEmployeeBalance={settleExpenseEmployeeBalance}
          settleRemark={settleExpenseRemark}
          setSettleRemark={setSettleExpenseRemark}
          submitting={settleExpenseSubmitting}
          onClose={() => setSettleExpenseModal(null)}
          onSubmit={handleConfirmSettleExpenseFromWallet}
        />
      )}
    </div>
  );
}
